import { forwardRef, type ReactNode, type Ref } from 'react';
import { Button, type ButtonProps } from '@hungryroot/ramen';
import { STARTING_ROUTE } from '../../defs/routes';
import { useNavigationResumeUrl } from '../../hooks/useNavigationResumeUrl';
import { usePromoCodeFromQueryParamsSessionStorage } from '../../hooks/usePromoCodeFromQueryParamsSessionStorage';
import {
  type AllFunnelRoutes,
  funnelRoutesSchema,
} from '../../navigation/funnel-routes';

type Props =
  | {
      prominence: ButtonProps['prominence'];
      style: ButtonProps['style'];
      size: ButtonProps['size'];
      accentColor?: ButtonProps['accentColor'];
      disabled?: boolean;
      resumeQuizText?: string;
      startQuizText?: string;
      className?: string;
      startingRoute?: AllFunnelRoutes;
      children?: never;
      ctaLink?: string;
      onClick?: () => void;
    }
  | {
      startingRoute?: AllFunnelRoutes;
      resumeQuizText?: string;
      startQuizText?: string;
      ctaLink?: string;
      children: (props: {
        text: string;
        route: AllFunnelRoutes | `/login/?next=${AllFunnelRoutes}`;
      }) => ReactNode;
    };

export const ResumeFunnelButton = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
    const {
      children,
      startQuizText = 'Take the Quiz',
      resumeQuizText = 'Finish Quiz',
      startingRoute = STARTING_ROUTE,
      ctaLink = undefined,
    } = props;

    // sometimes users are redirected to the homepage with a promo code in the URL params
    // we need to store the promo code in session storage so we can use it in /limited-time
    usePromoCodeFromQueryParamsSessionStorage();

    const result = useNavigationResumeUrl({
      routesSchema: funnelRoutesSchema,
      defaultRoute: startingRoute,
    });

    if (startingRoute === undefined) {
      return null;
    }

    if (children !== undefined) {
      return children({
        text: result === undefined ? startQuizText : resumeQuizText,
        route: result === undefined ? startingRoute : result,
      });
    }

    if (result === undefined) {
      return (
        <Button
          {...props}
          ref={ref}
          buttonType="link"
          to={ctaLink || startingRoute}
        >
          {startQuizText}
        </Button>
      );
    }

    return (
      <Button {...props} ref={ref} buttonType="link" to={ctaLink || result}>
        {resumeQuizText}
      </Button>
    );
  }
);
